/**
 * Demo feature catalog — single source of truth for the index page,
 * breadcrumbs, "components used here" cross-links, and route table.
 *
 * The shape mirrors `src/pages/{atoms,forms,flows}/` so adding a new
 * page is two edits:
 *   1. add a CatalogEntry here
 *   2. create the matching page file
 *
 * Routes resolve to `/${tier}/${id}`, e.g. `/atoms/auth-status-badge`.
 */

export type CatalogTier = 'atoms' | 'forms' | 'flows';

export interface CatalogEntry {
    /** Stable id used in URLs and cross-references. kebab-case. */
    id: string;
    /** Tier — drives column placement and route prefix. */
    tier: CatalogTier;
    /** Human-readable name. */
    name: string;
    /** Component name as it appears in import statements. */
    componentName: string;
    /** One-sentence summary for the catalog index. */
    summary: string;
    /** Longer description shown on the demo page header. */
    description: string;
    /** Cross-references — ids of other catalog entries used internally
     * by this component (rendered as "Components used here" on the
     * demo page). Leaves should leave this empty. */
    composedOf?: string[];
    /**
     * Sibling cross-references — components from a sibling tier that
     * pair naturally with this one (rendered as "Related" on the demo
     * page). Lets leaf forms still show navigation links to the
     * primitives they implicitly rely on.
     */
    relatedTo?: string[];
    /** Notes about limitations, stubs, server requirements. */
    notes?: string;
    /**
     * Optional canonical code example. Pages may read this via
     * `entry.code` instead of hardcoding their own snippet —
     * useful when the same example serves multiple consumers
     * (docs site, "copy from catalog" tooling, etc.). Today the
     * pages still inline their own examples; this field is the
     * forward-compat hook.
     */
    code?: string;
    /**
     * Optional default stage layout. When set, pages can omit
     * `stageLayout` from their `createDemoPage(...)` call and the
     * factory uses this. Per-page overrides still win.
     */
    stageLayout?: 'default' | 'center' | 'flow';
}

export const CATALOG: CatalogEntry[] = [
    /* ─── ATOMS ─────────────────────────────────────────────────────── */
    {
        id: 'auth-status-badge', tier: 'atoms', name: 'Auth Status Badge', componentName: 'AuthStatusBadge',
        summary: 'Pill showing the current auth status.',
        description: 'A tiny pill that renders the AuthClient snapshot status — "Signed in", "Signed out", "Loading", or "Offline". Useful in headers, footers, or admin debug bars.',
    },
    {
        id: 'user-avatar', tier: 'atoms', name: 'User Avatar', componentName: 'UserAvatar',
        summary: 'Initials avatar with deterministic color.',
        description: 'Renders the user\'s initials over a deterministic colored background derived from their id. Falls back to a "?" glyph when anonymous.',
    },
    {
        id: 'user-menu', tier: 'atoms', name: 'User Menu', componentName: 'UserMenu',
        summary: 'Header dropdown with identity + sign-out.',
        description: 'Avatar trigger that opens a panel with the user\'s name, email, current org, and a sign-out button. Closes on outside click and Escape.',
        composedOf: ['user-avatar', 'logout-button'],
    },
    {
        id: 'logout-button', tier: 'atoms', name: 'Logout Button', componentName: 'LogoutButton',
        summary: 'One-click sign-out for the current session.',
        description: 'Calls AuthClient.logoutCurrent(). Disabled while in-flight; configurable label and visual variant.',
    },
    {
        id: 'logout-all-button', tier: 'atoms', name: 'Logout All Button', componentName: 'LogoutAllButton',
        summary: 'Terminate every session everywhere.',
        description: 'Calls /auth/logout/all — revokes every refresh token AND bumps the per-user token-version, so outstanding access tokens fail validation cross-replica.',
    },
    {
        id: 'sso-button', tier: 'atoms', name: 'SSO Buttons', componentName: 'SsoButton',
        summary: 'Branded "Continue with X" buttons.',
        description: 'Base SsoButton + brand-themed variants for Google, Apple, Microsoft, and GitHub. Calls AuthClient.startSso() and navigates the browser to the provider.',
    },
    {
        id: 'sso-button-group', tier: 'atoms', name: 'SSO Button Group', componentName: 'SsoButtonGroup',
        summary: 'Pre-arranged stack of all four SSO buttons.',
        description: 'Drop-in for login pages — renders every built-in SSO provider in a vertical stack. Pass `providers` to filter or reorder.',
        composedOf: ['sso-button'],
    },
    {
        id: 'protected-route', tier: 'atoms', name: 'Protected Route', componentName: 'ProtectedRoute',
        summary: 'Render children only when authenticated.',
        description: 'Conditional wrapper — passes through when the AuthClient reports authenticated. Otherwise renders `fallback` (typically your router\'s redirect to /login).',
    },
    {
        id: 'guest-only', tier: 'atoms', name: 'Guest Only', componentName: 'GuestOnly',
        summary: 'Render children only when anonymous.',
        description: 'Inverse of ProtectedRoute. Useful for /login and /register pages that should redirect away when a signed-in user lands on them.',
    },
    {
        id: 'role-gate', tier: 'atoms', name: 'Role Gate', componentName: 'RoleGate',
        summary: 'Render children based on token roles.',
        description: 'Reads `claims.roles` from the AuthClient snapshot. Supports `anyOf` and `allOf` predicates. `system_admin` always passes (matches server-side bypass).',
    },
    {
        id: 'permission-gate', tier: 'atoms', name: 'Permission Gate', componentName: 'PermissionGate',
        summary: 'Render children based on token permissions.',
        description: 'Reads `claims.permissions` — resource:action strings. Useful for fine-grained UI gates without re-querying the server.',
    },
    {
        id: 'auth-loading', tier: 'atoms', name: 'Auth Loading', componentName: 'AuthLoading',
        summary: 'Show a spinner during AuthClient bootstrap.',
        description: 'Renders the fallback (default: an inline spinner) until `snapshot.ready === true`. Drop in app shell to avoid flashing anonymous UI before token restore completes.',
    },
    {
        id: 'token-expiry-countdown', tier: 'atoms', name: 'Token Expiry Countdown', componentName: 'TokenExpiryCountdown',
        summary: 'Live countdown to access-token expiry.',
        description: 'Re-renders once per second while authenticated. Reads `claims.exp` — stays consistent across tabs without an extra timer.',
        notes: 'Debug/visibility tool. Hide from end-users in production unless your security UX explicitly surfaces session timing.',
    },

    /* ─── FORMS ─────────────────────────────────────────────────────── */
    {
        id: 'login-form', tier: 'forms', name: 'Login Form', componentName: 'LoginForm',
        summary: 'Email + password with built-in 2FA path.',
        description: 'Standard credentials form. If the server responds with `requires_2fa`, the form swaps in a TOTP-code field and re-submits. Slots for SSO buttons and a "forgot" link.',
        relatedTo: ['sso-button-group', 'password-reset-request-form', 'register-form'],
    },
    {
        id: 'register-form', tier: 'forms', name: 'Register Form', componentName: 'RegisterForm',
        summary: 'Account creation with email + name.',
        description: 'Captures email, password, first/last name. Supports invite codes, invite tokens, and per-app email-domain validation (allowed_email_domains).',
        relatedTo: ['login-form', 'email-verification-notice', 'sso-button-group'],
    },
    {
        id: 'password-reset-request-form', tier: 'forms', name: 'Password Reset Request Form', componentName: 'PasswordResetRequestForm',
        summary: '"Forgot your password?" email request.',
        description: 'Anonymous form — collects email and POSTs /auth/password/reset-request. Always shows success messaging (anti-enumeration).',
        relatedTo: ['password-reset-form', 'login-form'],
    },
    {
        id: 'password-reset-form', tier: 'forms', name: 'Password Reset Form', componentName: 'PasswordResetForm',
        summary: 'Set a new password using a reset token.',
        description: 'Mount on the route the reset email links to. Takes `token` as a prop (typically from the URL search params), collects new password + confirm.',
        relatedTo: ['password-reset-request-form', 'change-password-form'],
    },
    {
        id: 'change-password-form', tier: 'forms', name: 'Change Password Form', componentName: 'ChangePasswordForm',
        summary: 'Rotate password (authenticated).',
        description: 'Server requires the current password as a re-auth defense. Tokens remain valid after the change unless you follow up with logoutAll().',
        relatedTo: ['logout-all-button', 'password-reset-form', 'sessions-list'],
    },
    {
        id: 'email-verification-notice', tier: 'forms', name: 'Email Verification Notice', componentName: 'EmailVerificationNotice',
        summary: '"Check your email" prompt with resend.',
        description: 'Post-register UI — shows the email being verified and a resend button. Email defaults to the AuthClient snapshot user.',
        relatedTo: ['register-form'],
    },
    {
        id: 'two-factor-enrollment', tier: 'forms', name: 'Two-Factor Enrollment', componentName: 'TwoFactorEnrollment',
        summary: 'QR + confirm-code 2FA setup wizard.',
        description: 'Two-step: enroll (server returns provisioningUri + base32 secret), then confirm with the first 6-digit code. The server flips `two_factor_confirmed_at` only after confirmation.',
        notes: 'QR code is rendered by api.qrserver.com (no client library). Swap by passing `renderQr`.',
        relatedTo: ['two-factor-disable-form', 'change-password-form'],
    },
    {
        id: 'two-factor-disable-form', tier: 'forms', name: 'Two-Factor Disable Form', componentName: 'TwoFactorDisableForm',
        summary: 'Turn off 2FA (password + code required).',
        description: 'Server requires both factors as defense against accidental / hijack-driven disable. Bumps token-version on success.',
        relatedTo: ['two-factor-enrollment', 'change-password-form'],
    },
    {
        id: 'sessions-list', tier: 'forms', name: 'Sessions List', componentName: 'SessionsList',
        summary: 'Active sessions table with terminate.',
        description: 'Lists every active session for the current user (device, IP, last-used). "Terminate" revokes one; terminating the current session logs the caller out.',
        relatedTo: ['logout-all-button', 'logout-button'],
    },
    {
        id: 'user-profile-card', tier: 'forms', name: 'User Profile Card', componentName: 'UserProfileCard',
        summary: 'Identity card with org + roles.',
        description: 'Read-only view of the AuthClient snapshot — avatar, name, email, org context, roles. Surfaces the impersonation banner when active.',
        composedOf: ['user-avatar'],
        relatedTo: ['change-password-form', 'sessions-list', 'org-switcher'],
    },
    {
        id: 'org-switcher', tier: 'forms', name: 'Org Switcher', componentName: 'OrgSwitcher',
        summary: 'Dropdown to switch organization context.',
        description: 'Lists the user\'s org memberships from /me/orgs and switches the token\'s org context via switchOrg(). The auth snapshot updates automatically.',
        relatedTo: ['user-profile-card', 'invitations-list'],
    },
    {
        id: 'user-lookup-table', tier: 'forms', name: 'User Lookup Table', componentName: 'UserLookupTable',
        summary: 'Admin: bulk-resolve users by email or id.',
        description: 'Paste a list of identifiers — the form parses out emails vs ids, hits POST /admin/users/lookup, and renders the result table. Requires an admin token.',
        relatedTo: ['users-table', 'admin-user-edit-panel'],
    },
    {
        id: 'members-list', tier: 'forms', name: 'Members List', componentName: 'MembersList',
        summary: 'Org members table with remove action.',
        description: 'Lists every member of the active (or specified) org with their roles + status. Requires org:members:read; remove action requires org:members:remove.',
        relatedTo: ['invite-member-form', 'invitations-admin-list', 'org-role-editor'],
    },
    {
        id: 'invite-member-form', tier: 'forms', name: 'Invite Member Form', componentName: 'InviteMemberForm',
        summary: 'Invite a teammate by email.',
        description: 'POSTs /orgs/{orgId}/invitations — server creates the invitation row and sends the email. Pair with InvitationsAdminList to see pending invites.',
        relatedTo: ['invitations-admin-list', 'members-list'],
    },
    {
        id: 'invitations-admin-list', tier: 'forms', name: 'Invitations (Org Admin)', componentName: 'InvitationsAdminList',
        summary: 'Pending invitations the org has sent out.',
        description: 'Lists pending invitations from the org-admin perspective with a revoke action. Pass `refreshKey` from the invite form to refetch after sending.',
        relatedTo: ['invite-member-form', 'members-list', 'invitations-list'],
    },
    {
        id: 'invitations-list', tier: 'forms', name: 'My Invitations', componentName: 'InvitationsList',
        summary: 'Invitations addressed to the current user.',
        description: 'Invitee-side list — every pending invitation for the caller. Accept auto-switches the active org via switchOrg() by default (disable via autoSwitch={false}).',
        composedOf: ['invitation-accept-card'],
        relatedTo: ['org-switcher', 'invitations-admin-list'],
    },
    {
        id: 'invitation-accept-card', tier: 'forms', name: 'Invitation Accept Card', componentName: 'InvitationAcceptCard',
        summary: 'Single-invitation focused accept/decline.',
        description: 'For an /accept-invite/{id} route — a focused single-invitation UI. Takes an InvitationRecord prop; emits onAccepted/onDeclined.',
        relatedTo: ['invitations-list', 'org-switcher'],
    },
    {
        id: 'org-role-editor', tier: 'forms', name: 'Org Role Editor', componentName: 'OrgRoleEditor',
        summary: 'Create/edit/delete custom org roles.',
        description: 'Two-column editor: roles on the left, name + description + permission picker on the right. Permission picker reads /orgs/{orgId}/permissions/assignable so system perms stay reserved.',
        relatedTo: ['members-list', 'permission-gate', 'role-gate'],
    },
    {
        id: 'org-settings-form', tier: 'forms', name: 'Org Settings Form', componentName: 'OrgSettingsForm',
        summary: 'Edit organization name and slug.',
        description: 'Fetches the full org record on mount (auth-snapshot org_name is stamped at token-issue time and may be stale). Requires org:read + org:update.',
        relatedTo: ['org-switcher', 'org-create-form'],
    },
    {
        id: 'org-create-form', tier: 'forms', name: 'Org Create Form', componentName: 'OrgCreateForm',
        summary: 'Admin: create a new organization.',
        description: 'POSTs /admin/organizations. Admin-only on the server. Wire onCreated to navigate or select the new org.',
        relatedTo: ['org-settings-form', 'org-switcher'],
    },
    {
        id: 'users-table', tier: 'forms', name: 'Users Table', componentName: 'UsersTable',
        summary: 'Admin: paginated user list with search.',
        description: 'GET /admin/users with debounced search + pagination. Each row composes UserAvatar; onRowClick is the navigation hook into the admin user-edit page.',
        composedOf: ['user-avatar'],
        relatedTo: ['admin-user-edit-panel', 'user-lookup-table'],
    },
    {
        id: 'admin-user-edit-panel', tier: 'forms', name: 'Admin User Edit Panel', componentName: 'AdminUserEditPanel',
        summary: 'Admin: edit roles + reset password + hard-delete.',
        description: 'Avatar + identity header, role toggle list, admin-side password reset, system_admin-gated hard-delete. Use under <RoleGate anyOf=["system_admin","super_admin"]>; the server gates as well.',
        composedOf: ['user-avatar'],
        relatedTo: ['users-table', 'role-gate', 'change-password-form'],
    },
    {
        id: 'magic-link-request-form', tier: 'forms', name: 'Magic-Link Request Form', componentName: 'MagicLinkRequestForm',
        summary: 'Passwordless: email me a sign-in link.',
        description: 'POSTs /auth/magic-link/request. Server is silent on whether the email is registered (anti-enumeration). Pair with a /auth/magic-link/verify route that calls verifyMagicLink() on the AuthClient.',
        relatedTo: ['login-form', 'password-reset-request-form'],
    },
    {
        id: 'delete-account-form', tier: 'forms', name: 'Delete Account Form', componentName: 'DeleteAccountForm',
        summary: 'Self-service permanent account deletion.',
        description: 'Requires current password + typed "DELETE" confirmation. On success, bumps token-version server-side so every device drops session immediately. Refuses if the user owns any organization — operator must transfer ownership first.',
        relatedTo: ['change-password-form', 'logout-all-button'],
    },
    {
        id: 'audit-log-table', tier: 'forms', name: 'Audit Log Table', componentName: 'AuditLogTable',
        summary: 'Admin: paginated audit-log viewer with filters.',
        description: 'GET /admin/audit-log with debounced action + user-id filters. Render under <RoleGate anyOf=["system_admin","super_admin"]>.',
        relatedTo: ['users-table', 'role-gate'],
    },

    /* ─── FLOWS ─────────────────────────────────────────────────────── */
    {
        id: 'complete-login-flow', tier: 'flows', name: 'Complete Login Flow', componentName: 'CompleteLoginFlow',
        summary: 'Full /login page (SSO + form + 2FA).',
        description: 'Opinionated composition — SSO buttons up top, a divider, then the password form (with 2FA prompt path built in), and a "create account" affordance below.',
        composedOf: ['sso-button-group', 'login-form'],
    },
    {
        id: 'complete-signup-flow', tier: 'flows', name: 'Complete Signup Flow', componentName: 'CompleteSignupFlow',
        summary: 'Full /register page → verify notice.',
        description: 'Captures account details, registers the user, then swaps in the verify-email notice. SSO buttons available for OAuth signup paths.',
        composedOf: ['sso-button-group', 'register-form', 'email-verification-notice'],
    },
    {
        id: 'complete-password-reset-flow', tier: 'flows', name: 'Complete Password Reset Flow', componentName: 'CompletePasswordResetFlow',
        summary: 'Branches on token presence — request or complete.',
        description: 'Single route that handles both "send me a reset link" (no token) and "set new password" (token present). Pass the URL\'s `token` search param as a prop.',
        composedOf: ['password-reset-request-form', 'password-reset-form'],
    },
    {
        id: 'complete-account-security-flow', tier: 'flows', name: 'Complete Account Security Flow', componentName: 'CompleteAccountSecurityFlow',
        summary: 'Full /settings/security page.',
        description: 'Change password, enable/disable 2FA, see active sessions, sign out of everywhere — all on one page.',
        composedOf: ['change-password-form', 'two-factor-enrollment', 'two-factor-disable-form', 'sessions-list', 'logout-all-button'],
    },
    {
        id: 'complete-sso-callback-flow', tier: 'flows', name: 'Complete SSO Callback Flow', componentName: 'CompleteSsoCallbackFlow',
        summary: 'Handle the OAuth redirect on /auth/callback.',
        description: 'Reads code + state from the URL search params, exchanges them via /auth/sso/callback (handles PKCE auth_code → /auth/sso/exchange transparently). Renders loading then success/error.',
        relatedTo: ['sso-button-group', 'complete-login-flow'],
    },
    {
        id: 'complete-impersonation-flow', tier: 'flows', name: 'Complete Impersonation Flow', componentName: 'CompleteImpersonationFlow',
        summary: 'Admin: impersonate another user with audit.',
        description: 'Pick a user id + reason, issue an impersonation token-pair. While impersonating, the panel shows the original admin so the action is traceable. Server enforces the actor-role gate.',
        relatedTo: ['users-table', 'admin-user-edit-panel', 'role-gate'],
    },
    {
        id: 'complete-org-admin-flow', tier: 'flows', name: 'Complete Org Admin Flow', componentName: 'CompleteOrgAdminFlow',
        summary: 'Full /settings/organization page.',
        description: 'One-page composition: settings, members, invite + pending invitations list, custom roles. Drop in at /settings/organization.',
        composedOf: ['org-settings-form', 'members-list', 'invite-member-form', 'invitations-admin-list', 'org-role-editor'],
    },
];

export const CATALOG_BY_TIER: Record<CatalogTier, CatalogEntry[]> = {
    atoms: CATALOG.filter((e) => e.tier === 'atoms'),
    forms: CATALOG.filter((e) => e.tier === 'forms'),
    flows: CATALOG.filter((e) => e.tier === 'flows'),
};

export const TIER_META: Record<CatalogTier, { title: string; description: string }> = {
    atoms: {
        title: 'Atoms',
        description: 'Single-purpose primitives. Tree-shake-friendly; compose them when you need full layout control.',
    },
    forms: {
        title: 'Forms & views',
        description: 'Multi-field, single-purpose views. Wire onSuccess / onError to navigate or toast.',
    },
    flows: {
        title: 'End-to-end flows',
        description: 'Full-screen, multi-step compositions. Drop straight into a route.',
    },
};

export function findEntry(tier: CatalogTier, id: string): CatalogEntry | undefined {
    return CATALOG.find((e) => e.tier === tier && e.id === id);
}

export function entryHref(entry: CatalogEntry): string {
    return `/${entry.tier}/${entry.id}`;
}
